import { useEffect, useMemo, useState } from 'react'
import {TrendingUp, Users, Star, BarChart2, Award, AlertCircle, ChevronDown, ChevronUp} from 'lucide-react'
import EmpresaLayout from '@/components/EmpresaLayout'
import { useAuth } from '@/hooks/useAuth'
import { useEvaluations, type Evaluation } from '@/hooks/useEvaluations'
import { EVALUATION_ASPECTS } from '@/lib/evaluationAspects'

// Colores para barras de aspectos
const BAR_COLORS = [
  'bg-amber-400', 'bg-blue-400', 'bg-emerald-400', 'bg-violet-400',
  'bg-rose-400', 'bg-teal-400', 'bg-orange-400', 'bg-indigo-400',
  'bg-pink-400', 'bg-cyan-400',
]

function scoreColor(avg: number): string {
  if (avg >= 8) return 'text-emerald-600'
  if (avg >= 6) return 'text-amber-600'
  return 'text-red-500'
}

function scoreBg(avg: number): string {
  if (avg >= 8) return 'bg-emerald-50 border-emerald-200'
  if (avg >= 6) return 'bg-amber-50 border-amber-200'
  return 'bg-red-50 border-red-200'
}

interface ColabStats {
  collaboratorUserId: string
  collaboratorName: string
  area: string
  totalEvals: number
  average: number
  lastPeriod: string
  aspectAverages: Record<string, number>
  trend: number // diff entre última evaluación y penúltima
}

function computeColabStats(evals: Evaluation[]): ColabStats[] {
  const byColab: Record<string, Evaluation[]> = {}
  evals.forEach((e) => {
    if (!byColab[e.collaboratorUserId]) byColab[e.collaboratorUserId] = []
    byColab[e.collaboratorUserId].push(e)
  })

  return Object.entries(byColab).map(([uid, evList]) => {
    const sorted = [...evList].sort((a, b) => b.period.localeCompare(a.period))
    const avgTotal =
      Math.round((sorted.reduce((s, e) => s + e.average, 0) / sorted.length) * 10) / 10

    // Promedio por aspecto
    const aspectAverages: Record<string, number> = {}
    EVALUATION_ASPECTS.forEach((asp) => {
      const vals = sorted.map((e) => Number(e.scores?.[asp.key]) || 0)
      aspectAverages[asp.key] = Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10
    })

    // Tendencia: diferencia entre la última y la penúltima evaluación
    const trend =
      sorted.length >= 2
        ? Math.round((sorted[0].average - sorted[1].average) * 10) / 10
        : 0

    return {
      collaboratorUserId: uid,
      collaboratorName: sorted[0]?.collaboratorName || uid,
      area: sorted[0]?.area || '—',
      totalEvals: sorted.length,
      average: avgTotal,
      lastPeriod: sorted[0]?.period || '—',
      aspectAverages,
      trend,
    }
  })
}

function EmpresaReportes() {
  const { user } = useAuth()
  const { evaluations, loading, fetchByCompany } = useEvaluations()
  const [expandedColab, setExpandedColab] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'nombre' | 'promedio' | 'evaluaciones'>('promedio')

  useEffect(() => {
    if (user) fetchByCompany(user.userId)
  }, [user])

  const stats = useMemo(() => computeColabStats(evaluations), [evaluations])

  // Promedios globales por aspecto (de toda la empresa)
  const globalAspectAverages = useMemo(() => {
    if (evaluations.length === 0) return {}
    const sums: Record<string, number> = {}
    EVALUATION_ASPECTS.forEach((asp) => {
      const vals = evaluations.map((e) => Number(e.scores?.[asp.key]) || 0)
      sums[asp.key] = Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10
    })
    return sums
  }, [evaluations])

  const globalAvg = useMemo(() => {
    if (stats.length === 0) return 0
    return Math.round((stats.reduce((s, c) => s + c.average, 0) / stats.length) * 10) / 10
  }, [stats])

  const sorted = useMemo(() => {
    return [...stats].sort((a, b) => {
      if (sortBy === 'promedio') return b.average - a.average
      if (sortBy === 'evaluaciones') return b.totalEvals - a.totalEvals
      return a.collaboratorName.localeCompare(b.collaboratorName)
    })
  }, [stats, sortBy])

  const areasMap = useMemo(() => {
    const map: Record<string, number[]> = {}
    stats.forEach((c) => {
      if (!map[c.area]) map[c.area] = []
      map[c.area].push(c.average)
    })
    return Object.entries(map).map(([area, avgs]) => ({
      area,
      avg: Math.round((avgs.reduce((s, v) => s + v, 0) / avgs.length) * 10) / 10,
      count: avgs.length,
    })).sort((a, b) => b.avg - a.avg)
  }, [stats])

  return (
    <EmpresaLayout
      title="Reportes de desempeño"
      subtitle="Promedios, tendencias y áreas de oportunidad de tu equipo"
    >
      {loading && evaluations.length === 0 ? (
        <div className="flex items-center gap-3 text-sm text-slate-400">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
          Cargando datos de evaluaciones…
        </div>
      ) : evaluations.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <BarChart2 size={36} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Sin datos de evaluaciones aún</p>
          <p className="mt-1 text-xs text-slate-400">
            Registra evaluaciones desde el módulo de Evaluaciones para ver los reportes aquí.
          </p>
        </div>
      ) : (
        <div className="space-y-6">

          {/* KPIs globales */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              { label: 'Colaboradores evaluados', value: stats.length, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Promedio global', value: globalAvg.toFixed(1), icon: Star, color: scoreColor(globalAvg), bg: globalAvg >= 8 ? 'bg-emerald-50' : globalAvg >= 6 ? 'bg-amber-50' : 'bg-red-50' },
              { label: 'Evaluaciones totales', value: evaluations.length, icon: BarChart2, color: 'text-violet-600', bg: 'bg-violet-50' },
              { label: 'Áreas distintas', value: areasMap.length, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-50' },
            ].map(({ label, value, icon: Icon, color, bg }) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${bg}`}>
                  <Icon size={18} className={color} />
                </div>
                <p className={`mt-3 text-2xl font-bold ${color}`}>{value}</p>
                <p className="mt-0.5 text-xs text-slate-500">{label}</p>
              </div>
            ))}
          </div>

          {/* Promedios globales por aspecto */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="mb-4 text-sm font-semibold text-slate-900">Promedio global por aspecto</h2>
            <div className="space-y-2.5">
              {EVALUATION_ASPECTS.map((asp, i) => {
                const val = globalAspectAverages[asp.key] ?? 0
                return (
                  <div key={asp.key} className="flex items-center gap-3">
                    <span className="w-40 shrink-0 text-xs text-slate-600 truncate">{asp.label}</span>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-2 rounded-full ${BAR_COLORS[i % BAR_COLORS.length]} transition-all`}
                        style={{ width: `${(val / 10) * 100}%` }}
                      />
                    </div>
                    <span className={`w-8 text-right text-xs font-semibold ${scoreColor(val)}`}>{val}</span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Promedios por área */}
          {areasMap.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h2 className="mb-4 text-sm font-semibold text-slate-900">Promedio por área</h2>
              <div className="space-y-2">
                {areasMap.map(({ area, avg, count }) => (
                  <div key={area} className="flex items-center gap-3">
                    <span className="w-36 shrink-0 text-xs text-slate-600 truncate">{area}</span>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all ${avg >= 8 ? 'bg-emerald-400' : avg >= 6 ? 'bg-amber-400' : 'bg-red-400'}`}
                        style={{ width: `${(avg / 10) * 100}%` }}
                      />
                    </div>
                    <span className={`w-8 text-right text-xs font-semibold ${scoreColor(avg)}`}>{avg}</span>
                    <span className="text-xs text-slate-400">({count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ranking de colaboradores */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-slate-900">Promedio por colaborador</h2>
              <div className="flex gap-2">
                {(['promedio', 'nombre', 'evaluaciones'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSortBy(s)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                      sortBy === s ? 'bg-slate-900 text-amber-400' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {s === 'promedio' ? 'Por promedio' : s === 'nombre' ? 'Por nombre' : 'Por # evaluaciones'}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {sorted.map((colab, idx) => {
                const isOpen = expandedColab === colab.collaboratorUserId
                return (
                  <div key={colab.collaboratorUserId} className={`rounded-xl border p-4 ${scoreBg(colab.average)}`}>
                    <div
                      className="flex cursor-pointer items-center justify-between gap-3"
                      onClick={() => setExpandedColab(isOpen ? null : colab.collaboratorUserId)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Ranking badge */}
                        {idx < 3 && (
                          <Award
                            size={16}
                            className={idx === 0 ? 'text-amber-500' : idx === 1 ? 'text-slate-400' : 'text-amber-700'}
                          />
                        )}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">{colab.collaboratorName}</p>
                          <p className="text-xs text-slate-500">
                            Área: {colab.area} · {colab.totalEvals} evaluación{colab.totalEvals !== 1 ? 'es' : ''} · Último: {colab.lastPeriod}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        {/* Tendencia */}
                        {colab.trend !== 0 && (
                          <span className={`text-xs font-medium ${colab.trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {colab.trend > 0 ? '▲' : '▼'} {Math.abs(colab.trend)}
                          </span>
                        )}
                        <span className={`text-xl font-bold ${scoreColor(colab.average)}`}>{colab.average}</span>
                        {isOpen ? (
                          <ChevronUp size={16} className="text-slate-400" />
                        ) : (
                          <ChevronDown size={16} className="text-slate-400" />
                        )}
                      </div>
                    </div>

                    {/* Desglose por aspecto */}
                    {isOpen && (
                      <div className="mt-4 space-y-2 border-t border-slate-200 pt-4">
                        {EVALUATION_ASPECTS.map((asp, i) => {
                          const val = colab.aspectAverages[asp.key] ?? 0
                          return (
                            <div key={asp.key} className="flex items-center gap-3">
                              <span className="w-36 shrink-0 text-xs text-slate-600">{asp.label}</span>
                              <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                                <div
                                  className={`h-1.5 rounded-full ${BAR_COLORS[i % BAR_COLORS.length]}`}
                                  style={{ width: `${(val / 10) * 100}%` }}
                                />
                              </div>
                              <span className={`w-8 text-right text-xs font-semibold ${scoreColor(val)}`}>{val}</span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Áreas de oportunidad (aspectos con menor promedio global) */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
            <div className="mb-3 flex items-center gap-2">
              <AlertCircle size={16} className="text-amber-600" />
              <h2 className="text-sm font-semibold text-amber-900">Áreas de oportunidad</h2>
            </div>
            <div className="space-y-1">
              {EVALUATION_ASPECTS
                .map((asp) => ({ ...asp, avg: globalAspectAverages[asp.key] ?? 0 }))
                .sort((a, b) => a.avg - b.avg)
                .slice(0, 3)
                .map((asp) => (
                  <div key={asp.key} className="flex items-center justify-between rounded-lg bg-white px-3 py-2">
                    <span className="text-sm text-slate-700">{asp.label}</span>
                    <span className={`text-sm font-bold ${scoreColor(asp.avg)}`}>{asp.avg}</span>
                  </div>
                ))}
            </div>
            <p className="mt-3 text-xs text-amber-700">
              Estos son los 3 aspectos con menor promedio en tu equipo. Considera acciones de mejora o capacitación.
            </p>
          </div>

        </div>
      )}
    </EmpresaLayout>
  )
}

export default EmpresaReportes
